import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { clampColumnWidth, COLUMN_CONSTRAINTS } from "../helper/columnWidthConstraints";

const applyWidthToElement = (el, width) => {
  if (!el) return;
  const inner = el.querySelector?.(".headerOfList");
  // With an inner `.headerOfList`, `width` is the content width, so the cell must also fit its padding.
  let cellWidth = width;
  if (inner) {
    const cs = getComputedStyle(el);
    cellWidth = width + (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    inner.style.width = `${width}px`;
  }
  el.style.width = `${cellWidth}px`;
  el.style.minWidth = `${cellWidth}px`;
  el.style.maxWidth = `${cellWidth}px`;
  el.style.setProperty("--column-resize-width", `${cellWidth}px`);
};

// Flex-layout header cells (div, not <th>) get their width from React on the next render;
// leftover min/max overrides would pin the header and stop it tracking later width changes.
const releaseFlexOverrides = el => {
  if (!el || el.tagName === "TH") return;
  el.style.minWidth = "";
  el.style.maxWidth = "";
};

/**
 * Shared column resize: pointer capture, live DOM width, Escape cancel, queued saves.
 *
 * @param {Object} options
 * @param {Function} options.getWidth - (columnId) => number
 * @param {Function} options.getMinWidth - (columnId) => number
 * @param {Function} [options.getMaxWidth] - (columnId) => number; defaults to the global max
 * @param {Function} options.onCommit - (columnId, width) => void — update React state
 * @param {Function} [options.onPersist] - (widthsByColumn) => void — batched save
 * @param {Function} [options.measureFit] - (columnId) => number
 */
export const useColumnResize = ({
  getWidth,
  getMinWidth,
  getMaxWidth,
  onCommit,
  onPersist,
  measureFit,
}) => {
  const dragRef = useRef(null);
  const saveQueueRef = useRef({});
  const getWidthRef = useRef(getWidth);
  const getMinWidthRef = useRef(getMinWidth);
  const getMaxWidthRef = useRef(getMaxWidth);
  const onCommitRef = useRef(onCommit);
  const measureFitRef = useRef(measureFit);

  getWidthRef.current = getWidth;
  getMinWidthRef.current = getMinWidth;
  getMaxWidthRef.current = getMaxWidth;
  onCommitRef.current = onCommit;
  measureFitRef.current = measureFit;

  const clampFor = useCallback((columnId, width) => {
    const minW = getMinWidthRef.current(columnId) ?? COLUMN_CONSTRAINTS.MIN_WIDTH;
    const maxW = getMaxWidthRef.current?.(columnId) ?? COLUMN_CONSTRAINTS.MAX_WIDTH;
    return clampColumnWidth(width, minW, maxW);
  }, []);

  const [dragOverlay, setDragOverlay] = useState(null);

  const flushSaveQueue = useCallback(() => {
    const widths = { ...saveQueueRef.current };
    if (Object.keys(widths).length === 0) return;
    saveQueueRef.current = {};
    onPersist?.(widths);
  }, [onPersist]);

  const debouncedFlushRef = useRef(
    debounce(() => {
      flushSaveQueue();
    }, 500),
  );

  useEffect(() => {
    const debounced = debouncedFlushRef.current;
    return () => {
      debounced.cancel();
      debounced.flush();
    };
  }, []);

  const queuePersist = useCallback((columnId, width) => {
    saveQueueRef.current[columnId] = width;
    debouncedFlushRef.current();
  }, []);

  const endDrag = useCallback(
    (commit, finalWidth) => {
      const drag = dragRef.current;
      if (!drag) return;

      document.body.classList.remove("disable-selection");
      document.body.style.cursor = "";

      if (commit && finalWidth != null && finalWidth !== drag.startWidth) {
        applyWidthToElement(drag.thElement, finalWidth);
        onCommitRef.current(drag.columnId, finalWidth);
        queuePersist(drag.columnId, finalWidth);
      } else {
        applyWidthToElement(drag.thElement, drag.startWidth);
      }
      releaseFlexOverrides(drag.thElement);

      dragRef.current = null;
      setDragOverlay(null);
    },
    [queuePersist],
  );

  const computeWidth = useCallback(
    (clientX, drag) => clampFor(drag.columnId, drag.startWidth + (clientX - drag.startX)),
    [clampFor],
  );

  useEffect(() => {
    const onPointerMove = e => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      e.preventDefault();
      const w = computeWidth(e.clientX, drag);
      applyWidthToElement(drag.thElement, w);
      setDragOverlay({
        columnId: drag.columnId,
        clientX: drag.startX + (w - drag.startWidth),
        width: w,
        tableRect: drag.tableElement?.getBoundingClientRect?.(),
      });
    };

    const onPointerUp = e => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const w = computeWidth(e.clientX, drag);
      endDrag(true, w);
    };

    const onKeyDown = e => {
      const drag = dragRef.current;
      if (drag && e.key === "Escape") {
        e.preventDefault();
        endDrag(false);
        return;
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [computeWidth, endDrag]);

  const startDrag = useCallback(
    (columnId, e, { thElement, tableElement }) => {
      if (!thElement || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const startWidth =
        getWidthRef.current(columnId) ||
        Math.round(thElement.getBoundingClientRect().width) ||
        COLUMN_CONSTRAINTS.DEFAULT_WIDTH;

      dragRef.current = {
        columnId,
        pointerId: e.pointerId,
        startX: e.clientX,
        startWidth,
        thElement,
        tableElement,
      };

      document.body.classList.add("disable-selection");
      document.body.style.cursor = "col-resize";

      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      setDragOverlay({
        columnId,
        clientX: e.clientX,
        width: startWidth,
        tableRect: tableElement?.getBoundingClientRect?.(),
      });
    },
    [],
  );

  const autoFit = useCallback(
    columnId => {
      const fit = measureFitRef.current?.(columnId);
      if (fit == null) return;
      const finalWidth = clampFor(columnId, fit);
      onCommitRef.current(columnId, finalWidth);
      queuePersist(columnId, finalWidth);
      const th =
        document.querySelector(`th[data-column-key="${columnId}"]`) ||
        document.querySelector(`[data-column-id="${columnId}"]`);
      applyWidthToElement(th, finalWidth);
      releaseFlexOverrides(th);
    },
    [clampFor, queuePersist],
  );

  const nudgeWidth = useCallback(
    (columnId, delta, thElement) => {
      const current =
        getWidthRef.current(columnId) ||
        (thElement ? Math.round(thElement.getBoundingClientRect().width) : COLUMN_CONSTRAINTS.DEFAULT_WIDTH);
      const next = clampFor(columnId, current + delta);
      applyWidthToElement(thElement, next);
      releaseFlexOverrides(thElement);
      onCommitRef.current(columnId, next);
      queuePersist(columnId, next);
    },
    [clampFor, queuePersist],
  );

  const getHandleProps = useCallback(
    (columnId, { ariaValueNow } = {}) => ({
      columnId,
      ariaValueNow: ariaValueNow ?? getWidthRef.current(columnId),
      onResizePointerDown: e => {
        const thElement =
          e.currentTarget.closest("th") || e.currentTarget.closest("[data-column-id]");
        const tableElement =
          e.currentTarget.closest("table") ||
          e.currentTarget.closest("[data-column-resize-root]");
        startDrag(columnId, e, { thElement, tableElement });
      },
      onAutoFit: () => autoFit(columnId),
      onKeyboardResize: (delta, el) => {
        const thElement = el?.closest?.("th") || el;
        nudgeWidth(columnId, delta, thElement);
      },
    }),
    [startDrag, autoFit, nudgeWidth],
  );

  return {
    getHandleProps,
    dragOverlay,
    flushSaveQueue,
  };
};
